import { useMemo, useState } from 'react'
import type { SelectOption } from '@/components/ui/Select'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import {
  ALL_ASSETS,
  EMPTY_RANGE,
  filterSubmissions,
  isSubmissionFilterActive,
  localDateDaysAgo,
  type AssetFilterValue,
  type DateRange,
  type FilterableRecording,
  type SubmissionFilter,
  type TimePeriod,
} from '@/lib/submissionFilter'
import type { Asset } from '@/types'

const ALL_ASSETS_OPTION: SelectOption = { id: ALL_ASSETS, label: 'All assets' }

// Asset choices are derived from the rows actually present so the dropdown only
// offers assets the user has submitted against; labels resolve via the taxonomy
// (falling back to the raw id when it hasn't loaded yet).
function buildAssetOptions(
  recordings: readonly FilterableRecording[],
  assets: readonly Asset[] | undefined,
): SelectOption[] {
  const labelById = new Map((assets ?? []).map((asset) => [asset.id, asset.label]))
  const labels = new Map<string, string>()
  for (const recording of recordings) {
    if (!labels.has(recording.assetId)) {
      labels.set(recording.assetId, labelById.get(recording.assetId) ?? recording.assetId)
    }
  }
  const options = [...labels.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return [ALL_ASSETS_OPTION, ...options]
}

export interface UseSubmissionFilterResult<T> {
  filter: SubmissionFilter
  assetOptions: SelectOption[]
  filteredRecordings: T[]
  isActive: boolean
  setAssetId: (assetId: AssetFilterValue) => void
  setPeriod: (period: TimePeriod) => void
  setRange: (range: DateRange) => void
  clear: () => void
}

/**
 * Owns the My Submissions filter: asset + time-period state, the derived asset
 * options, and the filtered rows. Filtering is client-side over the already
 * loaded pages, mirroring the design's in-memory model.
 */
export function useSubmissionFilter<T extends FilterableRecording>(
  recordings: readonly T[],
): UseSubmissionFilterResult<T> {
  const assets = useTaxonomyStore((state) => state.data?.assets)

  // Seed the custom range with the last 30 days so switching to it shows a
  // sensible window immediately, matching the design's default.
  const [filter, setFilter] = useState<SubmissionFilter>(() => ({
    assetId: ALL_ASSETS,
    period: 'all',
    range: { from: localDateDaysAgo(30), to: localDateDaysAgo(0) },
  }))

  const assetOptions = useMemo(
    () => buildAssetOptions(recordings, assets),
    [recordings, assets],
  )

  const filteredRecordings = useMemo(
    () => filterSubmissions(recordings, filter),
    [recordings, filter],
  )

  const setAssetId = (assetId: AssetFilterValue) =>
    setFilter((prev) => ({ ...prev, assetId }))
  const setPeriod = (period: TimePeriod) => setFilter((prev) => ({ ...prev, period }))
  const setRange = (range: DateRange) => setFilter((prev) => ({ ...prev, range }))
  // Reset the active dimensions but keep the user's chosen custom range around.
  const clear = () =>
    setFilter((prev) => ({ assetId: ALL_ASSETS, period: 'all', range: prev.range }))

  return {
    filter,
    assetOptions,
    filteredRecordings,
    isActive: isSubmissionFilterActive(filter),
    setAssetId,
    setPeriod,
    setRange,
    clear,
  }
}

export { EMPTY_RANGE }
